import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then(r => setProduct(r.data)).catch(err => console.error('API request failed:', err));
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) return <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center"><p className="text-[#6B7D8A] text-sm">Loading...</p></div>;

  return (
    <div className="bg-[#F0F9FF]">
      {/* HERO */}
      <section data-testid="product-hero" className="bg-[#0C4A8C] py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-[12px] mb-8 transition-colors">
            <ArrowLeft size={13} /> Back
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Product Image */}
            {product.image && (
              <div className="bg-[#F0F9FF] rounded-[3px] overflow-hidden aspect-square flex items-center justify-center p-8" data-testid="product-image">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
              </div>
            )}

            {/* Product Info */}
            <div>
              <p className="eyebrow mb-3 text-[#BAE6FD]">{product.tier === 'daily' ? 'TRUFORM MEN Daily' : 'TRUFORM MEN Pro'}</p>
              <h1 data-testid="product-name" className="text-3xl md:text-4xl font-serif font-medium text-white tracking-tight">{product.name}</h1>
              <p className="text-[14px] mt-3 font-light leading-relaxed text-white/45">{product.description}</p>

              <div className="flex flex-wrap gap-2 mt-5">
                {product.ph && !product.tags.some(t => t.toLowerCase().startsWith('ph')) && (
                  <span data-testid="product-ph-badge" className="text-[9px] font-sans uppercase tracking-[2px] px-3 py-1 rounded-[2px] bg-[#BAE6FD] text-[#062B52] font-semibold">pH {product.ph}</span>
                )}
                {product.tags.map((t, i) => (
                  <span key={i} className="text-[9px] font-sans uppercase tracking-[2px] px-3 py-1 rounded-[2px]" style={{ background: 'rgba(186,230,253,0.12)', color: 'rgba(255,255,255,0.6)' }}>{t}</span>
                ))}
              </div>

              <p className="mt-6">
                <span data-testid="product-price" className="text-2xl font-serif text-white font-medium">&#8377;{product.price}</span>
                <span className="text-[13px] ml-2 text-white/35">/ {product.size}</span>
              </p>

              <button data-testid="product-add-to-cart" onClick={() => addItem(product)}
                className="w-full mt-6 bg-[#F0F9FF] text-[#0C4A8C] py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-white transition-colors">
                Add to Cart — &#8377;{product.price}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* INGREDIENTS */}
      <section data-testid="ingredients-section" className="bg-white max-w-3xl mx-auto mt-5 rounded-[4px] py-12 md:py-16 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
        <p className="eyebrow text-[#0C7BC0] mb-3">Every ingredient. A reason.</p>
        <h2 className="text-2xl md:text-3xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-10">Why each one is here.</h2>

        <div className="flex flex-col gap-0">
          {product.ingredients.map((ing, i) => (
            <div key={i} className="flex gap-4 items-start py-5" style={{ borderBottom: i < product.ingredients.length - 1 ? '1px solid rgba(12,74,140,0.06)' : 'none' }}>
              <span className="font-serif text-2xl text-[#0C4A8C] font-medium flex-shrink-0 w-16">{ing.percentage}</span>
              <div>
                <h3 className="text-[14px] font-sans font-medium text-[#0C4A8C]">{ing.name}</h3>
                <p className="text-[13px] text-[#6B7D8A] font-light mt-1 leading-relaxed">{ing.reason}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW TO USE */}
      <section className="bg-white max-w-3xl mx-auto mt-5 rounded-[4px] py-12 md:py-16 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
        <p className="eyebrow text-[#0C7BC0] mb-3">How to use</p>
        <h2 className="text-2xl md:text-3xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-10">Simple by design.</h2>

        <div className="flex flex-col gap-0">
          {product.how_to_use.map((step, i) => (
            <div key={i} className="flex gap-4 items-start">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-[#0C4A8C] text-[#BAE6FD] flex items-center justify-center font-serif text-sm font-medium flex-shrink-0">{i + 1}</div>
                {i < product.how_to_use.length - 1 && <div className="w-px h-8" style={{ background: 'rgba(12,74,140,0.08)' }} />}
              </div>
              <p className="text-sm text-[#3D4F5C] font-light leading-relaxed pb-6">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HONEST VERSION */}
      <section data-testid="honest-version" className="bg-[#0C4A8C] py-12 md:py-16 mt-5 max-w-3xl mx-auto rounded-[4px]">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="eyebrow mb-3 text-[#BAE6FD]">The honest version</p>
          <h2 className="text-2xl font-serif font-medium text-white tracking-tight mb-6">Why TRUFORM MEN is different.</h2>
          <p className="font-serif italic text-[16px] leading-relaxed text-white/50">{product.honest_version}</p>
        </div>
      </section>

      {/* ADD TO CART */}
      <section className="max-w-3xl mx-auto py-10 px-6 md:px-10 text-center mb-5">
        <button data-testid="product-add-to-cart-bottom" onClick={() => addItem(product)}
          className="w-full bg-[#0C4A8C] text-[#F0F9FF] py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">
          Add to Cart — &#8377;{product.price}
        </button>
        <p className="text-[11px] text-[#6B7D8A] mt-3 font-light">Free shipping above &#8377;499 &middot; 3-5 day delivery &middot; Easy returns</p>
      </section>
    </div>
  );
}
